import React, {useCallback, useContext, useRef} from "react";
import {css, forBrands} from "@core-ui/react-stitch";
import {AccordionContext} from "./AccordionContext";
import {PanelContext} from "./PanelContext";
import {AccordionStyleFn, PanelProps} from "./types";
import {transitionTimeout} from "./PanelDetails";

const panelStyles: AccordionStyleFn = ({theme, crossBrand}) => {
  const borderColor = forBrands(
    theme,
    {
      at: (t) => `${t.color.g3}`,
      br: (t) => `${t.color.g4}`,
      brfs: (t) => `${t.color.g4}`,
      gap: (t) => `${t.color.g3}`,
      gapfs: (t) => `${t.color.g3}`,
      on: (t) => `${t.color.g4}`,
    },
    crossBrand
  );

  return css`
    border-top-color: ${borderColor};
    border-top-style: solid;
    border-top-width: 1px;

    &:last-of-type {
      border-bottom-color: ${borderColor};
      border-bottom-style: solid;
      border-bottom-width: 1px;
    }

    &:not(:hover) > div {
      transition: none;
    }
  `;
};

export function Panel({
  children: childrenProp,
  className,
  disabled,
  id,
  onChange: onChangeProp,
  scrollableElement,
}: PanelProps): JSX.Element {
  const {
    crossBrand,
    expanded,
    onChange,
    snap,
    snapToTopOnClose,
    unstyled,
    withChevron,
    withPlusMinus,
  } = useContext(AccordionContext);
  const [summary, ...children] = React.Children.toArray(childrenProp);
  const isExpanded = expanded?.includes(id);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback<React.ChangeEventHandler>(
    (event) => {
      onChange(event, id);
      if (onChangeProp) onChangeProp(event, id);
      const scrollerContainer = (
        panelRef: HTMLDivElement | any,
        transition: number
      ): void => {
        const accordionContainer =
          scrollableElement ?? panelRef?.current?.parentElement;
        if (accordionContainer) {
          setTimeout(() => {
            const panelY = panelRef?.current?.getBoundingClientRect()?.y ?? 0;
            const containerY = accordionContainer.getBoundingClientRect().y;
            accordionContainer.scrollBy({
              top: panelY - containerY,
              behavior: "smooth",
            });
          }, transition || 130);
        }
      };
      if (!isExpanded && snap && panelRef.current) {
        scrollerContainer(panelRef, transitionTimeout.enter);
      }
      if (isExpanded && snapToTopOnClose && panelRef.current) {
        scrollerContainer(panelRef, transitionTimeout.exit);
      }
    },
    [
      id,
      onChange,
      onChangeProp,
      panelRef,
      snap,
      isExpanded,
      snapToTopOnClose,
      scrollableElement,
    ]
  );
  return (
    <div
      ref={panelRef}
      className={className}
      css={(theme) => !unstyled && panelStyles({theme, crossBrand})}
    >
      <PanelContext.Provider
        value={{
          expanded: isExpanded,
          disabled,
          toggle: handleChange,
          withChevron,
          withPlusMinus,
        }}
      >
        {summary}
        {children}
      </PanelContext.Provider>
    </div>
  );
}
